import { useGLTF, useTexture } from '@react-three/drei'
import type { ObjectMap } from '@react-three/fiber'
import { useEffect, useMemo } from 'react'
import {
  Color,
  MeshLambertMaterial,
  MeshStandardMaterial,
  SRGBColorSpace,
  Texture,
  Vector2,
} from 'three'
import type { GLTF, GLTFLoader } from 'three-stdlib'

import { colormapColorChunk, colormapParsChunk } from '@shaders'
import { toLambert } from '@utils'

// --- CONFIGURATION CONSTANTS ---
const COLORMAP_TRAIN_PATH = '/textures/colormap-train.png'
const COLORMAP_BUILDING_PATH = '/textures/colormap-building.png'
const COLORMAP_FANTASY_PATH = '/textures/colormap-fantasy.png'

const colormapPaths = {
  train: COLORMAP_TRAIN_PATH,
  building: COLORMAP_BUILDING_PATH,
  fantasy: COLORMAP_FANTASY_PATH,
}

// --- TYPES ---
export type ColormapType = keyof typeof colormapPaths

export interface ColormapMaterialOptions {
  colorUV: Vector2
  colorUVThreshold: number
  color: string
}

type ColormapGLTFResult<T> = GLTF &
  ObjectMap & {
    nodes: T
    materials: {
      colormap: MeshStandardMaterial
    }
  }

class IgnoreColormapTexture {
  name = IgnoreColormapTexture.name
  parser: { json: { images?: { uri: string }[] } }
  constructor(parser: { json: { images?: { uri: string }[] } }) {
    this.parser = parser
  }
  loadTexture(): Promise<Texture> | null {
    const isColormap = this.parser.json.images?.find(i => i.uri === 'Textures/colormap.png')
    return isColormap ? Promise.resolve(new Texture()) : null
  }
}

// --- HOOKS ---

export function useColorMapGLTF<T>(
  path: string,
  colormapType: ColormapType,
  colormapOptions?: ColormapMaterialOptions,
): ColormapGLTFResult<T> & { material: MeshLambertMaterial } {
  const gltf = useGLTF(path, true, true, (loader: GLTFLoader) => {
    loader.register(parser => new IgnoreColormapTexture(parser))
  }) as ColormapGLTFResult<T>

  const texture = useTexture(colormapPaths[colormapType], texture => {
    texture.flipY = false
    texture.colorSpace = SRGBColorSpace
    return texture
  })

  const colorUVX = colormapOptions?.colorUV.x
  const colorUVY = colormapOptions?.colorUV.y
  const colorUVThreshold = colormapOptions?.colorUVThreshold
  const colorValue = colormapOptions?.color
  const hasColormapOverride =
    colorUVX !== undefined &&
    colorUVY !== undefined &&
    colorUVThreshold !== undefined &&
    colorValue !== undefined

  const uColor = useMemo(() => (colorValue ? new Color(colorValue) : null), [colorValue])

  const material = useMemo(() => {
    const material = toLambert(gltf.materials.colormap)
    material.map = texture
    material.customProgramCacheKey = () =>
      hasColormapOverride ? `colormap:${colorUVX}:${colorUVY}:${colorUVThreshold}` : 'colormap:base'

    if (!hasColormapOverride || !uColor) {
      material.needsUpdate = true
      return material
    }

    material.onBeforeCompile = shader => {
      shader.uniforms.uColor = { value: uColor }
      shader.fragmentShader = shader.fragmentShader
        .replace('#include <clipping_planes_pars_fragment>', colormapParsChunk)
        .replace(
          '#include <map_fragment>',
          colormapColorChunk(colorUVX, colorUVY, colorUVThreshold),
        )
      material.userData.shader = shader
    }

    material.needsUpdate = true
    return material
  }, [
    colorUVThreshold,
    colorUVX,
    colorUVY,
    gltf.materials.colormap,
    hasColormapOverride,
    texture,
    uColor,
  ])

  useEffect(() => {
    return () => {
      material.dispose()
    }
  }, [material])

  return { ...gltf, material }
}

// --- PRELOADER ---

useColorMapGLTF.preload = (path: string, type: ColormapType = 'train') => {
  useTexture.preload(colormapPaths[type])
  useGLTF.preload(path, true, true, (loader: GLTFLoader) => {
    loader.register(parser => new IgnoreColormapTexture(parser))
  })
}
